import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Stethoscope, 
  UserRound, 
  Activity, 
  Pill,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Stethoscope, UserRound, Activity, Pill
};

export const Services = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Our Services</h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto">
            Comprehensive healthcare solutions tailored to your needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-2 hover:border-[#14D1A2] transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-6">
                      <div className="w-16 h-16 bg-[#14D1A2]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-8 h-8 text-[#14D1A2]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                        <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                        <ul className="space-y-3">
                          {service.details.map((detail) => (
                            <li key={`${service.id}-${detail}`} className="flex items-start">
                              <CheckCircle2 className="w-5 h-5 text-[#14D1A2] mr-3 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Service Details</h2>
            <p className="text-lg text-gray-600">
              Learn more about what each service includes
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="opd" className="bg-white border-2 rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold text-gray-900 hover:text-[#14D1A2] py-6">
                OPD Consultations
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                <p className="mb-4">
                  Our Outpatient Department (OPD) provides comprehensive primary care services for patients of all ages. We focus on preventive care, early detection, and management of acute and chronic conditions.
                </p>
                <p className="mb-4">
                  <strong>What to expect:</strong> Your consultation includes a thorough examination, medical history review, diagnosis, treatment plan, and prescription if needed. We take the time to listen to your concerns and answer all your questions.
                </p>
                <p>
                  <strong>Walk-ins welcome</strong> during clinic hours, though appointments are recommended for shorter wait times.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="specialist" className="bg-white border-2 rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold text-gray-900 hover:text-[#14D1A2] py-6">
                Specialist Clinics
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                <p className="mb-4">
                  Access expert care from our panel of experienced specialists across multiple medical disciplines. Each specialist brings years of expertise and is committed to providing evidence-based, patient-centered care.
                </p>
                <p className="mb-4">
                  <strong>Specialties available:</strong> Urology, Pulmonology, Nephrology, and Endocrinology. All specialists follow international treatment protocols and guidelines.
                </p>
                <p>
                  <strong>Booking:</strong> Specialist consultations require prior appointments. Contact us to check availability and book your slot.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="diagnostics" className="bg-white border-2 rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold text-gray-900 hover:text-[#14D1A2] py-6">
                Diagnostic Services
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                <p className="mb-4">
                  Our in-house diagnostic center is equipped with modern equipment to provide accurate and timely test results. We maintain strict quality control standards to ensure reliability.
                </p>
                <p className="mb-4">
                  <strong>Available tests:</strong> Complete blood count, lipid profiles, diabetes tests, thyroid function tests, liver and kidney function tests, ECG, X-rays, and comprehensive health screening packages.
                </p>
                <p className="mb-4">
                  <strong>Sample collection:</strong> Home sample collection available for your convenience. Digital reports sent via email and SMS.
                </p>
                <p>
                  <strong>Turnaround time:</strong> Most routine tests within 24 hours, urgent tests within 4-6 hours.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="pharmacy" className="bg-white border-2 rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold text-gray-900 hover:text-[#14D1A2] py-6">
                In-House Pharmacy
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                <p className="mb-4">
                  Our well-stocked pharmacy ensures you get your prescribed medications immediately after consultation. We maintain a comprehensive inventory of essential medicines, chronic disease medications, and health supplements.
                </p>
                <p className="mb-4">
                  <strong>Quality assurance:</strong> All medicines sourced from authorized distributors. Proper storage conditions maintained. Licensed pharmacists available for medication counseling.
                </p>
                <p className="mb-4">
                  <strong>Services:</strong> Medicine dispensing, dosage instructions, drug interaction checks, and medication reminders.
                </p>
                <p>
                  <strong>Home delivery:</strong> Available for refills and maintenance medications. Call us to place your order.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1C5C62] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Book an appointment today and experience quality healthcare
          </p>
          <Link to="/book-appointment">
            <Button size="lg" className="bg-[#14D1A2] hover:bg-[#10B88A] text-white text-lg px-10 h-14">
              Book Appointment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
